import React, { useCallback, type FC } from 'react'
import { Col, Row } from 'react-bootstrap'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { ROUTER_BASEPATH } from '../../../../common/constants'
import { BannerSlot, BannerStack } from '../../../../components/BannerStack/BannerStack'
import { BANNER_PRIORITY } from '../../bannerPriority'
import { McpPromoBanners } from '../../McpPromo/McpPromoBanners'
import { McpPromoPage } from '../../McpPromo/mcpPromoConfig'
import { useContactsEmptyState, useFinancialPlanningOnboardingBanner } from './useContactsPageOnboardingBanners'
import { ContactsEmptyState } from './ContactsEmptyState/ContactsEmptyState'
import { FinancialPlanningOnboardingBanner } from './FinancialPlanningOnboardingBanner/FinancialPlanningOnboardingBanner'

interface ContactsPageOnboardingBannersProps {
  contactCount: number | undefined
  isContactCountUnknown: boolean
}

interface ContactsEmptyStateSlotProps {
  contactCount: number | undefined
  isContactCountUnknown: boolean
  postConnectRedirectPath: (serviceId: IntegrationServiceId) => string
}

const ContactsEmptyStateSlot: FC<ContactsEmptyStateSlotProps> = ({ contactCount, isContactCountUnknown, postConnectRedirectPath }) => {
  const { shouldShow, isFetching, isDismissing, dismiss, allowedServiceIds, connectedWithoutSync, displayCategory } = useContactsEmptyState()

  const applies = contactCount === 0

  return (
    <BannerSlot
      id='contactsEmptyState'
      priority={BANNER_PRIORITY.EMPTY_STATE}
      isFetching={isContactCountUnknown || (applies && (isFetching || isDismissing))}
      shouldShow={applies && shouldShow && displayCategory !== null}
    >
      {displayCategory !== null ? (
        <Row className='zocks-mb-2'>
          <Col className='d-flex justify-content-center'>
            <ContactsEmptyState
              displayCategory={displayCategory}
              serviceIds={allowedServiceIds}
              connectedWithoutSync={connectedWithoutSync}
              postConnectRedirectPath={postConnectRedirectPath}
              onDismiss={dismiss}
              isDismissing={isDismissing}
            />
          </Col>
        </Row>
      ) : null}
    </BannerSlot>
  )
}

interface FinancialPlanningSlotProps {
  contactCount: number | undefined
  isContactCountUnknown: boolean
}

const FinancialPlanningSlot: FC<FinancialPlanningSlotProps> = ({ contactCount, isContactCountUnknown }) => {
  const { shouldShow, isFetching, isDismissing, dismiss } = useFinancialPlanningOnboardingBanner()

  const applies = (contactCount ?? 0) > 0

  return (
    <BannerSlot
      id='contactsFinancialPlanning'
      priority={BANNER_PRIORITY.FINANCIAL_PLANNING_ONBOARDING}
      isFetching={isContactCountUnknown || (applies && (isFetching || isDismissing))}
      shouldShow={applies && shouldShow}
    >
      <Row className='zocks-mb-2'>
        <Col>
          <FinancialPlanningOnboardingBanner onDismiss={dismiss} />
        </Col>
      </Row>
    </BannerSlot>
  )
}

const McpPromoSlot: FC = () => (
  <Row className='zocks-mb-2'>
    <Col>
      <McpPromoBanners page={McpPromoPage.CONTACTS} />
    </Col>
  </Row>
)

export const ContactsPageOnboardingBanners: React.FC<ContactsPageOnboardingBannersProps> = ({ contactCount, isContactCountUnknown }) => {
  const postConnectRedirectPath = useCallback((serviceId: IntegrationServiceId) => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    return `${ROUTER_BASEPATH}/onboarding/post-connect/${serviceId}?returnTo=${returnTo}&intent=contacts`
  }, [])

  return (
    <BannerStack>
      <ContactsEmptyStateSlot contactCount={contactCount} isContactCountUnknown={isContactCountUnknown} postConnectRedirectPath={postConnectRedirectPath} />
      <FinancialPlanningSlot contactCount={contactCount} isContactCountUnknown={isContactCountUnknown} />
      <McpPromoSlot />
    </BannerStack>
  )
}
